import * as React from 'react';
import { useEffect, useState } from 'react';
import { Material, Obra } from '../../types';
import { usePermisosContext } from '../../context/PermisosContext';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
  useMediaQuery,
  Fab,
  Stack,
  Chip,
  alpha,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Done as DoneIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  AddCircle as AddCircleIcon,
  Inventory2 as InventoryIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { collection, query, onSnapshot, where, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { MaterialCard } from './MaterialCard';

interface Props {
  obras: Obra[];
  obraSeleccionada: string;
}

const validationSchema = yup.object({
  items: yup.array().of(
    yup.object({
      nombre: yup.string().required('El nombre es requerido'),
      cantidad: yup.number().required('La cantidad es requerida').min(1, 'La cantidad debe ser mayor a 0'),
    })
  ),
  obraId: yup.string().required('La obra es requerida'),
});

export const MaterialesTable: React.FC<Props> = ({ obras, obraSeleccionada }) => {
  const [materiales, setMateriales] = useState<Material[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [selectedTab, setSelectedTab] = useState('pendientes');
  const [isSimpleView, setIsSimpleView] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [materialType, setMaterialType] = useState<'manual' | 'foto'>('manual');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { usuario } = useAuth();
  const { verificarPermisosObra, puedeEditar } = usePermisosContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!usuario) return;

    const materialesRef = collection(db, 'materiales');
    const unsubscribes: (() => void)[] = [];

    const role = usuario.role?.toUpperCase();
    const isRestricted = role !== 'ADMIN' && !usuario.puedeVerTodasLasObras;

    const processAndSetMateriales = (materialsList: Material[]) => {
      const processed = materialsList.map(m => ({
        ...m,
        obraNombre: obras.find(o => o.id === m.obraId)?.nombre || m.obraNombre || 'Obra desconocida'
      }));
      setMateriales(processed);
    };

    if (obraSeleccionada) {
      // Case 1: Specific work selected
      const q = query(materialesRef, where('obraId', '==', obraSeleccionada));
      const unsub = onSnapshot(q, (snapshot) => {
        const materials = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Material));
        processAndSetMateriales(materials);
      });
      unsubscribes.push(unsub);

    } else if (isRestricted) {
      // Case 2: "Todas las obras" for restricted user
      const assignedObras = usuario.obrasAsignadas || [];

      if (assignedObras.length === 0) {
        setMateriales([]);
        return;
      }

      const chunks = [];
      for (let i = 0; i < assignedObras.length; i += 10) {
        chunks.push(assignedObras.slice(i, i + 10));
      }

      // Store results from each chunk
      const results: Record<number, Material[]> = {};

      chunks.forEach((chunk, index) => {
        const q = query(materialesRef, where('obraId', 'in', chunk));
        const unsub = onSnapshot(q, (snapshot) => {
          const materials = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Material));

          results[index] = materials;

          // Merge all results whenever any chunk updates
          const allMaterials = Object.values(results).flat();
          processAndSetMateriales(allMaterials);
        });
        unsubscribes.push(unsub);
      });

    } else {
      // Case 3: "Todas las obras" for Admin/Full Access
      const q = query(materialesRef);
      const unsub = onSnapshot(q, (snapshot) => {
        const materials = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Material));
        processAndSetMateriales(materials);
      });
      unsubscribes.push(unsub);
    }

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [obraSeleccionada, usuario, obras]);

  const handleMaterialOperation = async (operation: 'add' | 'update' | 'delete' | 'toggleComprado', values?: any, materialId?: string) => {
    setError(null);
    try {
      const targetObraId = values?.obraId || (materialId ? materiales.find(m => m.id === materialId)?.obraId : null);

      // Verify permissions
      // If we have an obraId, we check specific permissions for that work
      if (targetObraId) {
        if (!verificarPermisosObra(targetObraId)) {
          setError('No tienes permisos para realizar esta acción en esta obra');
          return;
        }
      } else {
        // If we DON'T have an obraId (orphan material), only allow if user has global edit permissions (Admin/Editor with global view)
        // We assume if they can see it (checked by the query logic in useEffect), and they are Admin/Editor, they can clean it up.
        // But let's be strict: if it's an "orphan", maybe we only allow delete?
        // Let's rely on puedeEditar() which checks for role 'ADMIN' or 'EDITOR'.
        const isAdminOrSuper = usuario?.role === 'ADMIN' || usuario?.puedeVerTodasLasObras;

        if (!isAdminOrSuper) {
          setError('No se pudo determinar la obra y no tienes permisos globales para forzar la operación');
          return;
        }
      }

      // Check for write permission (Editor or Admin)
      if (!puedeEditar()) {
        setError('Solo administradores y editores pueden modificar materiales');
        return;
      }

      switch (operation) {
        case 'add':
          if (materialType === 'foto') {
            if (!selectedFile) {
              setError('Por favor selecciona una imagen para subir');
              return;
            }
            // Upload photo logic
            const storageRef = ref(storage, `materiales/${Date.now()}_${selectedFile.name}`);
            const snapshot = await uploadBytes(storageRef, selectedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);

            const materialData = {
              nombre: values.items[0].nombre, // Use the first item name as the list name
              cantidad: 1, // Represents 1 list
              obraId: targetObraId,
              unidadMedida: 'lista',
              comprado: false,
              usuarioEmail: usuario?.email,
              usuarioNombre: usuario?.displayName || usuario?.email || 'Usuario',
              fechaCreacion: serverTimestamp(),
              fechaRequerida: Timestamp.now(),
              esFoto: true,
              fotoUrl: downloadURL,
              fotoPath: storageRef.fullPath
            };
            await addDoc(collection(db, 'materiales'), materialData);
          } else {
            // Existing logic for manual items
            const items = values.items || [];
            const batchPromises = items.map((item: any) => {
              const materialData = {
                nombre: item.nombre,
                cantidad: item.cantidad,
                obraId: targetObraId,
                unidadMedida: 'u',
                comprado: false,
                usuarioEmail: usuario?.email,
                usuarioNombre: usuario?.displayName || usuario?.email || 'Usuario',
                fechaCreacion: serverTimestamp(),
                fechaRequerida: Timestamp.now(),
                esFoto: false
              };
              return addDoc(collection(db, 'materiales'), materialData);
            });
            await Promise.all(batchPromises);
          }
          break;
        case 'update':
          if (!materialId) return;
          // For update, we assume single item edit
          const item = values.items[0];
          const docRef = doc(db, 'materiales', materialId);
          await updateDoc(docRef, {
            nombre: item.nombre,
            cantidad: item.cantidad,
            obraId: targetObraId, // Ensure we write the obraId if provided
          });
          break;
        case 'delete':
          if (!materialId) return;
          // Check if it has a photo to delete
          const materialToDelete = materiales.find(m => m.id === materialId);
          if (materialToDelete?.fotoPath) {
            try {
              const photoRef = ref(storage, materialToDelete.fotoPath);
              await deleteObject(photoRef);
            } catch (e) {
              console.error("Error deleting photo:", e);
            }
          }
          await deleteDoc(doc(db, 'materiales', materialId));
          break;
        case 'toggleComprado':
          if (!materialId) return;
          const newCompradoState = !values.comprado;

          let updateData: any = {
            comprado: newCompradoState
          };

          // If marking as bought and it is a photo, delete the photo
          if (newCompradoState) {
            const mat = materiales.find(m => m.id === materialId);
            if (mat?.esFoto && mat?.fotoPath) {
              try {
                const photoRef = ref(storage, mat.fotoPath);
                await deleteObject(photoRef);
                // Clear photo references from doc
                updateData.fotoUrl = null;
                updateData.fotoPath = null;
              } catch (e) {
                console.error("Error deleting photo on buy:", e);
              }
            }
          }

          await updateDoc(doc(db, 'materiales', materialId), updateData);
          break;
      }

      if (operation === 'add' || operation === 'update') {
        handleCloseDialog();
      }
    } catch (error) {
      console.error(error);
      setError('Ocurrió un error al procesar la operación');
    }
  };

  const formik = useFormik({
    initialValues: {
      items: [{ nombre: '', cantidad: 1 }],
      obraId: obraSeleccionada || (obras.length === 1 ? obras[0].id : ''),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await handleMaterialOperation(
          editingMaterial ? 'update' : 'add',
          values,
          editingMaterial?.id
        );
        if (!error) {
          resetForm();
        }
      } catch (err) {
        setError('Error al guardar material');
      }
    },
  });


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleOpenPreview = (url: string) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handleOpenDialog = (material?: Material) => {
    if (material) {
      setEditingMaterial(material);
      formik.setValues({
        items: [{ nombre: material.nombre, cantidad: material.cantidad }],
        obraId: material.obraId,
      });
    } else {
      setEditingMaterial(null);
      formik.resetForm();
      formik.setFieldValue('items', [{ nombre: '', cantidad: 1 }]);
      formik.setFieldValue('obraId', obraSeleccionada || (obras.length === 1 ? obras[0].id : ''));
      setMaterialType('manual');
      setSelectedFile(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMaterial(null);
    formik.resetForm();
  };

  const handleToggleComprado = async (material: Material) => {
    // Pass the current comprado state directly to avoid race conditions
    // Also pass obraId to avoid lookup issues in the handler
    await handleMaterialOperation('toggleComprado', { comprado: material.comprado, obraId: material.obraId }, material.id);
  };

  const handleDeleteMaterial = async (material: Material) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este material?')) {
      await handleMaterialOperation('delete', { obraId: material.obraId }, material.id);
    }
  };

  const materialesFiltrados = materiales
    .filter(material => selectedTab === 'pendientes' ? !material.comprado : material.comprado)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const contarMaterialesPorEstado = () => {
    const pendientes = materiales.filter(m => !m.comprado).length;
    const comprados = materiales.filter(m => m.comprado).length;
    return { pendientes, comprados };
  };

  const { pendientes, comprados } = contarMaterialesPorEstado();

  return (
    <Box sx={{ width: '100%', position: 'relative', minHeight: '60vh' }}>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 48,
            }
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Pendientes</span>
                <Chip
                  label={pendientes}
                  size="small"
                  color={selectedTab === 'pendientes' ? 'primary' : 'default'}
                  sx={{ height: 20, minWidth: 20, px: 0.5 }}
                />
              </Box>
            }
            value="pendientes"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Comprados</span>
                <Chip
                  label={comprados}
                  size="small"
                  color={selectedTab === 'comprados' ? 'success' : 'default'}
                  sx={{ height: 20, minWidth: 20, px: 0.5 }}
                />
              </Box>
            }
            value="comprados"
          />
        </Tabs>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={!puedeEditar()}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            Nuevo Material
          </Button>
        )}
      </Box>

      {/* Contenido */}
      {materialesFiltrados.length === 0 ? (
        <Box sx={{
          py: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          color: 'text.secondary',
          bgcolor: 'grey.50',
          borderRadius: 3,
          border: '1px dashed',
          borderColor: 'grey.300'
        }}>
          <InventoryIcon sx={{ fontSize: 60, color: 'grey.300' }} />
          <Typography variant="h6" color="text.secondary">
            No hay materiales {selectedTab === 'pendientes' ? 'pendientes' : 'comprados'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedTab === 'pendientes'
              ? 'Agrega nuevos materiales para comenzar'
              : 'Los materiales que marques como comprados aparecerán aquí'}
          </Typography>
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Stack spacing={0}>
              {materialesFiltrados.map((material, index) => (
                <MaterialCard
                  key={material.id || `material-${index}`}
                  material={material}
                  onToggleComprado={handleToggleComprado}
                  onEdit={handleOpenDialog}
                  onDelete={handleDeleteMaterial}
                  puedeEditar={puedeEditar()}
                  showObra={!isSimpleView}
                  onViewPhoto={handleOpenPreview}
                />
              ))}
            </Stack>
          ) : (
            <TableContainer sx={{
              borderRadius: 3,
              overflowX: 'auto',
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <Table
                sx={{
                  minWidth: 650,
                  '& .MuiTableCell-root': {
                    borderColor: 'grey.100'
                  }
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Nombre</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Cantidad</TableCell>

                    {!isSimpleView && (
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Obra</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center', color: 'text.secondary' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materialesFiltrados.map((material, index) => (
                    <TableRow
                      key={material.id || `material-row-${index}`}
                      sx={{ '&:hover': { bgcolor: 'grey.50' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {material.nombre}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.70rem' }}>
                          {material.usuarioNombre || material.usuarioEmail || 'Usuario descon.'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={material.cantidad} size="small" variant="outlined" />
                      </TableCell>

                      {!isSimpleView && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {material.obraNombre}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          {material.esFoto && material.fotoUrl && (
                            <IconButton
                              onClick={() => handleOpenPreview(material.fotoUrl!)}
                              size="small"
                              sx={{ color: 'info.main', bgcolor: alpha(theme.palette.info.main, 0.05) }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Checkbox
                            checked={material.comprado}
                            onChange={() => handleToggleComprado(material)}
                            disabled={!puedeEditar()}
                            size="small"
                            color="success"
                          />
                          {puedeEditar() && (
                            <>
                              <IconButton
                                onClick={() => handleOpenDialog(material)}
                                size="small"
                                sx={{ color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteMaterial(material)}
                                size="small"
                                sx={{ color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.05) }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {isMobile && puedeEditar() && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 80, // Above bottom navigation if exists, or just bottom
            right: 16,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
          }}
          onClick={() => handleOpenDialog()}
        >
          <AddIcon />
        </Fab>
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
          {editingMaterial ? 'Editar Material' : 'Nuevo Material'}
        </DialogTitle>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogContent>

              <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
                {!obraSeleccionada && (
                  <FormControl fullWidth error={formik.touched.obraId && Boolean(formik.errors.obraId)}>
                    <InputLabel>Obra</InputLabel>
                    <Select
                      name="obraId"
                      value={formik.values.obraId}
                      onChange={formik.handleChange}
                      label="Obra"
                    >
                      {obras.filter(obra => verificarPermisosObra(obra.id)).map((obra) => (
                        <MenuItem key={obra.id} value={obra.id}>
                          {obra.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.obraId && formik.errors.obraId && (
                      <Typography color="error" variant="caption" sx={{ ml: 2, mt: 0.5 }}>
                        {formik.errors.obraId}
                      </Typography>
                    )}
                  </FormControl>
                )}

                {!editingMaterial && (
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Tipo de Entrada</FormLabel>
                    <RadioGroup
                      row
                      value={materialType}
                      onChange={(e) => {
                        const newType = e.target.value as 'manual' | 'foto';
                        setMaterialType(newType);
                        if (newType === 'foto') {
                          // Keep the first item's name but reset quantity and remove other items
                          const firstItemName = formik.values.items[0]?.nombre || '';
                          formik.setFieldValue('items', [{ nombre: firstItemName, cantidad: 1 }]);
                        }
                      }}
                    >
                      <FormControlLabel value="manual" control={<Radio />} label="Items Manuales" />
                      <FormControlLabel value="foto" control={<Radio />} label="Foto de Lista" />
                    </RadioGroup>
                  </FormControl>
                )}

                {materialType === 'foto' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      name="items.0.nombre" // Reuse first item name for the list name
                      label="Título de la Lista / Descripción"
                      value={formik.values.items[0].nombre}
                      onChange={formik.handleChange}
                      error={formik.touched.items?.[0]?.nombre && Boolean((formik.errors.items as any)?.[0]?.nombre)}
                      helperText={formik.touched.items?.[0]?.nombre && (formik.errors.items as any)?.[0]?.nombre}
                    />

                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {selectedFile ? selectedFile.name : 'Subir Imagen'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </Button>
                    {selectedFile && (
                      <Typography variant="caption" color="success.main">
                        Imagen seleccionada: {selectedFile.name}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <FieldArray
                    name="items"
                    render={arrayHelpers => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {formik.values.items.map((item, index) => (
                          <Box key={index} sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <TextField
                                fullWidth
                                name={`items.${index}.nombre`}
                                label="Nombre del Material"
                                value={item.nombre}
                                onChange={formik.handleChange}
                                error={
                                  formik.touched.items?.[index]?.nombre &&
                                  Boolean((formik.errors.items as any)?.[index]?.nombre)
                                }
                                helperText={
                                  formik.touched.items?.[index]?.nombre &&
                                  (formik.errors.items as any)?.[index]?.nombre
                                }
                                size="small"
                              />
                              <TextField
                                fullWidth
                                name={`items.${index}.cantidad`}
                                label="Cantidad"
                                type="number"
                                value={item.cantidad}
                                onChange={formik.handleChange}
                                error={
                                  formik.touched.items?.[index]?.cantidad &&
                                  Boolean((formik.errors.items as any)?.[index]?.cantidad)
                                }
                                helperText={
                                  formik.touched.items?.[index]?.cantidad &&
                                  (formik.errors.items as any)?.[index]?.cantidad
                                }
                                size="small"
                              />
                            </Box>
                            {!editingMaterial && (
                              <IconButton
                                onClick={() => arrayHelpers.remove(index)}
                                disabled={formik.values.items.length === 1}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}

                        {!editingMaterial && (
                          <Button
                            startIcon={<AddCircleIcon />}
                            onClick={() => arrayHelpers.push({ nombre: '', cantidad: 1 })}
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 2 }}
                          >
                            Agregar otro material
                          </Button>
                        )}
                      </Box>
                    )}
                  />
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancelar</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formik.isSubmitting}
                sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
              >
                {formik.isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Guardar'}
              </Button>
            </DialogActions>
          </form>
        </FormikProvider>
      </Dialog >

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ position: 'relative', bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>
    </Box >
  );
};